/**
 * 经纪委托发布（求购/求售、数量、价格区间、佣金）
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publishBrokerOrder } from '@/api/broker';
import { BREEDS, BULK_THRESHOLD } from '@/utils/constants';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/broker.css';

export default function BrokerOrderNew() {
  const navigate = useNavigate();
  const [type, setType] = useState(1);
  const [breed, setBreed] = useState('');
  const [quantity, setQuantity] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [commissionRate, setCommissionRate] = useState('2.0');
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const qtyNum = Number(quantity);
  const isBulk = qtyNum >= BULK_THRESHOLD;

  const onSubmit = async () => {
    setError('');
    if (!breed) {
      setError('请选择品种');
      return;
    }
    if (qtyNum < 1) {
      setError('数量需 ≥1 头');
      return;
    }
    if (!priceMin || !priceMax) {
      setError('请填写价格区间');
      return;
    }
    if (Number(priceMax) < Number(priceMin)) {
      setError('最高价不能低于最低价');
      return;
    }
    setSubmitting(true);
    try {
      await publishBrokerOrder({
        type,
        breed,
        quantity: qtyNum,
        price_min: Number(priceMin),
        price_max: Number(priceMax),
        commission_rate: Number(commissionRate) / 100,
        remark,
      });
      navigate('/broker/orders');
    } catch (e) {
      setError(e instanceof Error ? e.message : '发布失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="broker-page">
      <PageHero eyebrow="BROKER ORDER" title="经纪委托发布" desc="委托平台牛经纪为您求购 / 求售，专业撮合更高效" />
      <div className="container broker-form">
        <div className="broker-section">
          <div className="broker-section__head">委托类型</div>
          <div className="form-radio-row">
            {[
              [1, '求购', '我要买牛，找经纪帮我找货'],
              [2, '求售', '我要卖牛，找经纪帮我找买家'],
            ].map(([v, l, d]) => (
              <label className={`broker-type-card ${type === v ? 'active' : ''}`} key={v}>
                <input type="radio" name="broker_type" checked={type === v} onChange={() => setType(v as number)} />
                <strong>{l}</strong>
                <span>{d}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="broker-section">
          <div className="broker-section__head">委托内容</div>
          <div className="broker-form-grid">
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>品种</label>
              <select className="form-control" value={breed} onChange={(e) => setBreed(e.target.value)}>
                <option value="">请选择品种</option>
                {BREEDS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>数量（头）</label>
              <input
                className="form-control"
                type="number"
                min={1}
                placeholder="≥1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              {isBulk ? (
                <div className="form-help">大宗订单（≥{BULK_THRESHOLD} 头）：价格可面议</div>
              ) : null}
            </div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>最低价（元/头）</label>
              <input
                className="form-control"
                type="number"
                placeholder="价格下限"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>最高价（元/头）</label>
              <input
                className="form-control"
                type="number"
                placeholder="价格上限"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>佣金比例（%）</label>
              <input
                className="form-control"
                type="number"
                step="0.1"
                min={0}
                max={10}
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
              />
              <div className="form-help">平台建议区间 1.5%~3%；成交后自动从成交款划扣</div>
            </div>
          </div>
        </div>

        <div className="broker-section">
          <div className="broker-section__head">佣金设置</div>
          <div className="broker-form-grid">
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>佣金比例（%）</label>
              <input
                className="form-control"
                type="number"
                step="0.1"
                min={0}
                max={10}
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
              />
              <div className="form-help">平台建议区间 1.5%~3%；成交后自动从成交款划扣</div>
            </div>
            <div className="form-group">
              <label className="form-label">备注</label>
              <textarea
                className="form-control"
                placeholder="补充要求（体重、健康、检疫等）"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          </div>
        </div>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="broker-submit">
          <button className="btn btn-ghost" onClick={() => navigate('/broker/orders')}>取消</button>
          <button className="btn btn-primary btn-lg" disabled={submitting} onClick={onSubmit}>
            {submitting ? '发布中…' : '发布委托'}
          </button>
        </div>
      </div>
    </div>
  );
}
